<template>
  <section class="section pricing-all">
    <div class="container">
      <h2 class="section__title pricing-all__title" v-html="translations.title" />
      <div class="pricing-all__items">
        <template v-for="item in itemsList">
          <figure @click="handleModalShow(item)">
            <img
              :src="`/static/promo-static/landing-2021/src/images/pages/price/index/pricing-all/icons/${item}.svg`"
              width="252"
              height="164"
              alt=""
            />
            <figcaption v-html="items[item].caption" />
          </figure>
        </template>
      </div>
    </div>
    <div
      ref="overlay"
      v-if="modalShow"
      :class="$style.overlay"
      :data-animate="modalAnimate"
      @click="handleModalHideOverlay"
    >
      <div :class="$style.overlayInner">
        <div :class="$style.box">
          <button :class="$style.closeButton" @click="handleModalHide">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              :class="[$style.closeIcon, 'close-icon']"
            >
              <path stroke-width="3" d="m2 2 20.003 20.002M22.002 2 2 22.002" />
            </svg>
          </button>
          <div :class="$style.boxInner">
            <article v-if="modalFor" :class="$style.content">
              <header :class="$style.header">
                <img
                  :src="`/static/promo-static/landing-2021/src/images/pages/price/index/pricing-all/icons/${modalFor}.svg`"
                  :class="$style.icon"
                  width="252"
                  height="164"
                  alt=""
                />
                <h1 v-html="items[modalFor].modal.title" :class="$style.title" />
              </header>
              <p v-html="items[modalFor].modal.text" :class="$style.text" />
              <footer :class="$style.footer">
                <button
                  type="button"
                  v-html="items[modalFor].modal.button"
                  class="button button--primary"
                  @click="handleDemoRequestModalOpen"
                />
              </footer>
            </article>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getScrollbarWidth } from '~scripts/core/helpers';
import translations from './pricing-translations';

export default {
  name: 'PricingTable',
  data() {
    return {
      modalFor: '',
      modalShow: false,
      modalAnimate: '',
    };
  },
  computed: {
    region() {
      return window.region;
    },
    lang() {
      return this.region === 'ai' ? 'en' : 'ru';
    },
    translations() {
      return translations[this.lang];
    },
    items() {
      return this.translations.items;
    },
    itemsList() {
      return Object.keys(this.items);
    },
  },
  mounted() {
    window.addEventListener('keyup', this.handleModalHideEsc);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.handleModalHideEsc);
  },
  methods: {
    handleModalShow(item) {
      const scrollbarWidth = getScrollbarWidth();

      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.body.classList.add('no-scroll');

      this.modalFor = item;
      this.modalShow = true;
      this.modalAnimate = 'show';
    },
    handleModalHide(removeScroll = true) {
      this.modalAnimate = 'hide';

      this.$refs.overlay.addEventListener(
        'animationend',
        () => {
          this.modalShow = false;

          if (removeScroll) {
            document.body.classList.remove('no-scroll');
            document.documentElement.style.removeProperty('--scrollbar-width');
          }

          this.modalFor = '';
        },
        {
          once: true,
        }
      );
    },
    handleModalHideOverlay(e) {
      if (e.target === this.$refs.overlay) {
        this.handleModalHide();
      }
    },
    handleModalHideEsc(e) {
      if (e.code === 'Escape' && this.modalShow) {
        this.handleModalHide();
      }
    },
    handleDemoRequestModalOpen() {
      this.handleModalHide(false);

      const modalAction = document.querySelector('[data-modal="#demoRequestModal"]');
      modalAction.click();
    },
  },
};
</script>

<style module lang="scss">
@use '~styles/core/_variables.scss' as vars;
@use '~styles/core/_mixins.scss' as mixins;

// Overlay
@keyframes showOverlay {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
@keyframes hideOverlay {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.overlay {
  position: fixed;
  z-index: 3001;
  inset: 0;
  display: flex;
  padding: 60px 20px;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  opacity: 0;
  animation-duration: vars.$transitionDurationSlow;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;
  will-change: opacity;

  &[data-animate='show'] {
    animation-name: showOverlay;
  }

  &[data-animate='hide'] {
    animation-name: hideOverlay;
  }
}

.overlayInner {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
}

// Box
@keyframes showBox {
  0% {
    opacity: 0;
    transform: translateY(-50px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hideBox {
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(-50px);
  }
}

.box {
  max-width: 440px;

  @include mixins.media(md) {
    position: relative;
    max-width: 600px;
    opacity: 0;
    transform: translateY(-50px);
    animation-duration: vars.$transitionDurationSlow;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    will-change: transform, opacity, scroll-position;

    @at-root [data-animate='show'] {
      .box {
        animation-name: showBox;
      }
    }

    @at-root [data-animate='hide'] {
      .box {
        animation-name: hideBox;
      }
    }
  }
}

@keyframes showBoxInner {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes hideBoxInner {
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(-20px);
  }
}

.boxInner {
  padding: 4rem 2.4rem;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 18px 66px rgba(46, 51, 67, 0.12);

  @include mixins.media(sm, max) {
    opacity: 0;
    transform: translateY(-20px);
    animation-duration: vars.$transitionDurationSlow;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
    will-change: transform, opacity, scroll-position;

    @at-root [data-animate='show'] {
      .boxInner {
        animation-name: showBoxInner;
      }
    }

    @at-root [data-animate='hide'] {
      .boxInner {
        animation-name: hideBoxInner;
      }
    }
  }

  @include mixins.media(md) {
    padding: 6rem;
    border-radius: 28px;
  }
}

// Close
.closeButton {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  padding: 20px;
  cursor: pointer;

  @include mixins.hover {
    &:hover {
      .closeIcon > path {
        stroke: vars.$colorSystem;
      }
    }
  }

  @include mixins.media(md) {
    @include mixins.size(46px);

    top: 20px;
    right: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: unset;
    background-color: rgba(#fff, 0.5);
    backdrop-filter: blur(5px);
    border-radius: 50%;
  }
}

.closeIcon {
  vertical-align: top;

  > path {
    stroke: rgba(vars.$colorBase, 0.4);
  }
}

// Content
.content {
  text-align: center;
}

.header {
  margin-bottom: 1rem;
}

.icon {
  @include mixins.size(130px, auto);

  margin-bottom: 1.2rem;

  @include mixins.media(md) {
    @include mixins.size(252px, auto);

    margin-bottom: 2.4rem;
  }
}

.title,
.text {
  font-weight: vars.$fontWeightBase;
}

.title {
  @include mixins.font(2.1rem, 1.2);

  @include mixins.media(md) {
    @include mixins.font(3.5rem, 1.18);
  }
}

.text {
  @include mixins.font(1.4rem, 1.6);

  opacity: 0.5;

  @include mixins.media(md) {
    @include mixins.font(1.9rem, 1.55);
  }
}

.footer {
  margin-top: 2rem;

  @include mixins.media(md) {
    margin-top: 3.2rem;
  }
}
</style>
